import { IPortfolioPerformancePosition } from '../../../../../providers/portfolio-performance/portfolio-performance.types';
import {
  classifyInstrument,
  expirySortKey,
  parseOptionExpiry,
  PositionType,
} from './classify-position';

export interface PositionGroup {
  key: string;
  label: string;
  rows: IPortfolioPerformancePosition[];
  totalPv: number;
  totalPosition: number;
  // Only Options groups have children (subgrouped by expiry) — Token/Futures
  // rows render directly under their type group.
  children?: PositionGroup[];
}

const sumBy = (rows: IPortfolioPerformancePosition[], key: 'pv' | 'position'): number =>
  rows.reduce((acc, r) => acc + r[key], 0);

const TYPE_ORDER: { type: PositionType; label: string }[] = [
  { type: 'token', label: 'Token' },
  { type: 'futures', label: 'Futures' },
  { type: 'options', label: 'Options' },
];

// Deribit-style tree: Type (Token/Futures/Options) at the top, Options
// further split by expiry — see the account card's reference screenshot.
// Only types that actually have rows (after the caller's own type filter is
// applied) appear at all.
export const buildPositionGroups = (
  positions: IPortfolioPerformancePosition[],
): PositionGroup[] => {
  const byType: Record<PositionType, IPortfolioPerformancePosition[]> = {
    token: [],
    futures: [],
    options: [],
  };
  positions.forEach((p) => byType[classifyInstrument(p.instrument)].push(p));

  const groups: PositionGroup[] = [];

  for (const { type, label } of TYPE_ORDER) {
    const rows = byType[type];
    if (rows.length === 0) continue;

    if (type !== 'options') {
      groups.push({
        key: type,
        label,
        rows,
        totalPv: sumBy(rows, 'pv'),
        totalPosition: sumBy(rows, 'position'),
      });
      continue;
    }

    const byExpiry = new Map<string, IPortfolioPerformancePosition[]>();
    rows.forEach((p) => {
      const expiry = parseOptionExpiry(p.instrument) ?? 'Other';
      const existing = byExpiry.get(expiry);
      if (existing) existing.push(p);
      else byExpiry.set(expiry, [p]);
    });

    const children: PositionGroup[] = Array.from(byExpiry.entries())
      .sort(([a], [b]) => expirySortKey(a) - expirySortKey(b))
      .map(([expiry, expiryRows]) => ({
        key: `options-${expiry}`,
        label: expiry,
        rows: expiryRows,
        totalPv: sumBy(expiryRows, 'pv'),
        totalPosition: sumBy(expiryRows, 'position'),
      }));

    groups.push({
      key: 'options',
      label,
      rows: [],
      totalPv: sumBy(rows, 'pv'),
      totalPosition: sumBy(rows, 'position'),
      children,
    });
  }

  return groups;
};
